// Updated: 2026-08-04 20:50:00 CDT
// Purpose: Provide shared Supabase auth state without logging session credentials.

import React, { createContext, useEffect, useMemo, useState } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabaseClient";

type AuthContextValue = {
  session: Session | null;
  user: User | null;
  isLoading: boolean;
  signOut: () => Promise<void>;
};

export const AuthContext = createContext<AuthContextValue>({
  session: null,
  user: null,
  isLoading: true,
  signOut: async () => {},
});

export const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initial load
  useEffect(() => {
    let isMounted = true;
    let authRevision = 0;
    const initialRevision = authRevision;
    (async () => {
      try {
        const {
          data: { session: initialSession },
          error,
        } = await supabase.auth.getSession();

        if (error) {
          // eslint-disable-next-line no-console
          console.error("[AuthProvider] getSession error:", error);
        }

        if (!isMounted || authRevision !== initialRevision) return;

        setSession(initialSession ?? null);
        setUser(initialSession?.user ?? null);
        // eslint-disable-next-line no-console
        console.debug("[AuthProvider] Initial session resolved:", {
          hasSession: Boolean(initialSession),
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[AuthProvider] unexpected getSession error:", err);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    })();

    // Listen for auth state changes
    const { data: subscription } = supabase.auth.onAuthStateChange(
      (event, nextSession) => {
        if (!isMounted) return;
        authRevision++;
        // eslint-disable-next-line no-console
        console.debug("[AuthProvider] onAuthStateChange:", { event, hasSession: Boolean(nextSession) });
        setSession(nextSession ?? null);
        setUser(nextSession?.user ?? null);
        setIsLoading(false);
      }
    );

    return () => {
      isMounted = false;
      subscription.subscription?.unsubscribe();
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user,
      isLoading,
      signOut: async () => {
        try {
          const { error } = await supabase.auth.signOut();
          if (error) {
            // eslint-disable-next-line no-console
            console.error("[AuthProvider] signOut error:", error);
          } else {
            // eslint-disable-next-line no-console
            console.debug("[AuthProvider] signOut success");
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("[AuthProvider] unexpected signOut error:", err);
        }
      },
    }),
    [session, user, isLoading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
